import {
  FC,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import GLP from 'google-libphonenumber';

import logo from '/logo.svg';

import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import qs from 'qs';
import { TFunction } from 'i18next';
import { UseMutationResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import Input from 'components/Input';
import Button from 'components/Button';
import sendCodeForChangeOrConfirmPhoneMutation from 'api/mutations/sendCodeForChangeOrConfirmPhoneMutation';
import { agent } from 'api/agent';
import Timer from 'components/Timer';
import sendTwofaCodeMutation from 'api/mutations/sendTwofaCodeMutation';
import enableTwoFaMutation from 'api/mutations/enableTwoFaMutation';
import useUser from 'api/hooks/useUser';
import twoFaRequestCodeMutation from 'api/mutations/twoFaRequestCodeMutation';
import codeRequestToPhoneOrEmailMutation from 'api/mutations/codeRequestToPhoneOrEmailMutation';
import Loading from 'components/Loading';
import { queryClient } from 'utils/queryClient';
import SecureStorage from 'utils/localStorage';

const PNF = GLP.PhoneNumberFormat;

const phoneUtil = GLP.PhoneNumberUtil.getInstance();

const types = {
  phoneNumber: 'phoneNumber',
  mfa: 'mfa',
};

const confirmCodeMutationsByType = {
  phoneNumber: sendCodeForChangeOrConfirmPhoneMutation,
  mfa: sendTwofaCodeMutation,
};

const sendCodeMutationsByType = {
  phoneNumber: codeRequestToPhoneOrEmailMutation,
  mfa: twoFaRequestCodeMutation,
};

const getTitleByType = (
  type: keyof typeof types,
  user: any,
  t: TFunction<'translation', undefined>,
) => {
  switch (type) {
    case types.mfa: {
      return t('multifactorAuthentication');
    }
    default: {
      if (user?.phoneNumber) {
        return t('phoneNumber');
      }

      return t('welcomeToEvs');
    }
  }
};

interface IConfirmCodeProps {
  type: keyof typeof types;
}

const ConfirmCode: FC<IConfirmCodeProps> = ({ type }) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const { data: user } = useUser();

  const [codeVerificationError, setCodeVerificationError] = useState<
  string | null
  >(null);
  const [isSendCode, setIsSendCode] = useState(true);
  const { mutate: confirmCodeMutate, isPending } =
    confirmCodeMutationsByType[type]();
  const { mutate: sendCodeMutate, error: sendCodeError } =
    sendCodeMutationsByType[type]() as UseMutationResult<
    AxiosResponse<any, any>,
    Error,
    any,
    unknown
    >;
  const { mutate: enableTwoFaMutate } = enableTwoFaMutation();

  const { phoneNumber = user?.phoneNumber || '', prev } = qs.parse(search, {
    ignoreQueryPrefix: true,
  }) as { [key: string]: string | undefined };

  const isPhoneDisplay = !user?.phoneNumber || type === types.mfa;

  const effectRanRef = useRef(false);

  useLayoutEffect(() => {
    if (effectRanRef.current) {
      return;
    }

    effectRanRef.current = true;

    onSendCode();

    return () => setCodeVerificationError(null);
  }, []);

  const formattedPhone = useMemo(() => {
    try {
      if (phoneNumber && isPhoneDisplay) {
        return phoneUtil
          .format(
            phoneUtil.parseAndKeepRawInput(`+${phoneNumber}`),
            PNF.INTERNATIONAL,
          )
          .trim();
      }

      return `+${phoneNumber.trim()}`;
    } catch (error) {
      // Handle phone number parsing error gracefully
      return `+${phoneNumber.trim()}`;
    }
  }, [phoneNumber, isPhoneDisplay]);

  useEffect(() => {
    if (type === 'phoneNumber' && !phoneNumber) {
      navigate(prev || '/');
    }
  }, [phoneNumber, type, navigate, prev]);

  const toChangePhoneNumber = () => {
    navigate('/auth/set-phone-number');
  };

  const onSendCode = () => {
    if (isSendCode) {
      setCodeVerificationError(null);
      let data = {};

      if (type === types?.phoneNumber) {
        data = { phoneNumber: `+${phoneNumber.replace('+', '').trim()}` };
      }

      sendCodeMutate(data as any, {
        onSuccess: () => {
          setIsSendCode(false);
        },
      });
    }
  };

  const onSubmit = async () => {
    confirmCodeMutate(
      {
        code: value,
      },
      {
        onSuccess: async (res) => {
          setCodeVerificationError(null);

          // Handle tokens from the response
          if (res.data?.token) {
            SecureStorage.setItem('token', res.data.token, {
              secure: true,
            });
            SecureStorage.setItem('refreshToken', res.data.refreshToken, {
              secure: true,
            });
            agent.getClient(false).defaults.headers.common[
              'Authorization'
            ] = `Bearer ${res.data.token}`;
          }

          if (res.data?.user) {
            await queryClient.setQueryData(['user'], res.data?.user);
          }

          const resUser = res.data?.user || {
            ...user,
            phoneNumber: res.data?.phoneNumber || user?.phoneNumber,
          };

          switch (type) {
            case 'phoneNumber': {
              queryClient.setQueryData(['user'], resUser);

              if (!resUser.isTwoFAEnabled && resUser?.phoneNumber) {
                enableTwoFaMutate(
                  {
                    isTwoFAEnabled: true,
                  },
                  {
                    onSuccess: () => {
                      navigate('/');
                    },
                  },
                );
              } else {
                SecureStorage.setItem('mfa', 'true');
                await queryClient.invalidateQueries({ queryKey: ['user'] });

                navigate('/');
              }

              break;
            }
            case 'mfa': {
              SecureStorage.setItem('token', res.data.token, { secure: true });
              SecureStorage.setItem('mfa', 'true');
              SecureStorage.setItem('refreshToken', res.data.refreshToken, {
                secure: true,
              });
              await queryClient.invalidateQueries({ queryKey: ['user'] });

              agent.getClient(false).defaults.headers.common[
                'Authorization'
              ] = `Bearer ${res.data.token}`;

              navigate('/');

              break;
            }
          }

          SecureStorage.setItem('user_id', resUser.id, { secure: true });
        },
        onError() {
          setCodeVerificationError('Code invalid or expired');
        },
      },
    );
  };

  return (
    <div className="flex flex-col w-full items-center justify-center max-h-screen">
      <div className="flex flex-col border py-14 px-10 rounded-2xl border-[#CBCBCB] bg-[#fff] text-center md:px-2 md:py-8">
        <div className="justify-center items-center flex">
          <img src={logo} alt="logo" />
        </div>
        <div className="text-3xl font-bold my-[12px] justify-center items-center flex md:text-2xl">
          {getTitleByType(type, user, t)}
        </div>
        <div className="text-base text-[#757575] md:text-sm w-[75%] mx-auto">
          <span
            dangerouslySetInnerHTML={{
              __html: isPhoneDisplay
                ? t('confirmationPhoneCodeMessage')
                : t('confirmationEmailCodeMessage', {
                  email: `<span class="underline text-black">${user.email}</span>`,
                }),
            }}
          ></span>
          {isPhoneDisplay && (
            <span className="inline-block ml-1 font-bold">
              {formattedPhone}
            </span>
          )}
        </div>

        <div className="my-6 w-3/4 md:w-2/3 mx-auto">
          <Input
            className="w-full md:text-sm md:h-10"
            title={t('code')}
            value={value}
            arrowRightClassName="top-1/2 right-0 transform -translate-x-1/2 -translate-y-1/2 md:right-10"
            titleClassName="top-[-9px] left-[8px]"
            onChange={setValue}
            maxLength={6}
            placeholder="Enter code"
          />
          {codeVerificationError && (
            <span className="text-[#FF3B30] text-xs">
              {codeVerificationError}
            </span>
          )}
        </div>

        <div
          className={`text-primary mb-2 max-w-max mx-auto font-bold ${
            isSendCode ? 'underline cursor-pointer' : ''
          }`}
          onClick={onSendCode}
        >
          <span className={`${!isSendCode ? 'font-normal text-gray-500' : ''}`}>
            Send code again
          </span>
          {!isSendCode && (
            <Timer
              callback={() => {
                setIsSendCode(true);
              }}
            />
          )}
        </div>
        {sendCodeError && (
          <div className="text-red-800">{sendCodeError?.message}</div>
        )}
        {type === types.mfa && (
          <div
            className="text-primary font-bold underline mb-6 cursor-pointer max-w-max mx-auto"
            onClick={toChangePhoneNumber}
          >
            Change your phone number
          </div>
        )}
        <div className="flex justify-center">
          <Button
            onClick={onSubmit}
            disabled={value.length < 6}
            btnType="primary"
          >
            {isPending ? <Loading size={25} /> : t('continue')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmCode;
